// Documentation content - imported from content/docs/taias-lib
// MDX files are compiled to React components at build time

import { ComponentType } from "react";
import IntroductionMdx from "@/content/docs/taias-lib/getting-started/introduction.mdx";
import QuickstartMdx from "@/content/docs/taias-lib/getting-started/quickstart.mdx";
import FlowsAndDecisionsMdx from "@/content/docs/taias-lib/core-concepts/flows-and-decisions.mdx";
import UiAffordancesMdx from "@/content/docs/taias-lib/core-concepts/ui-affordances.mdx";
import AdvancedPatternsMdx from "@/content/docs/taias-lib/core-concepts/advanced-patterns.mdx";
import ObservabilityMdx from "@/content/docs/taias-lib/core-concepts/observability.mdx";
import DevModeMdx from "@/content/docs/taias-lib/core-concepts/dev-mode.mdx";
import ApiReferenceMdx from "@/content/docs/taias-lib/reference/api-reference.mdx";
import TypesMdx from "@/content/docs/taias-lib/reference/types.mdx";
import docsMetadata from "./docs-metadata.json";

export interface DocHeading {
  id: string;
  text: string;
  level: 2 | 3;
}

export interface DocSection {
  id: string;
  title: string;
  Component: ComponentType;
  headings: DocHeading[];
  rawContent: string;
}

export interface DocGroup {
  label: string;
  sections: DocSection[];
}

type MetadataEntry = {
  headings: DocHeading[];
  rawContent: string;
};

const metadata = docsMetadata as Record<string, MetadataEntry>;

export const docGroups: DocGroup[] = [
  {
    label: "Getting Started",
    sections: [
      {
        id: "introduction",
        title: "Introduction",
        Component: IntroductionMdx,
        headings: [...(metadata.introduction?.headings ?? [])],
        rawContent: metadata.introduction?.rawContent ?? "",
      },
      {
        id: "quickstart",
        title: "Quickstart",
        Component: QuickstartMdx,
        headings: [...(metadata.quickstart?.headings ?? [])],
        rawContent: metadata.quickstart?.rawContent ?? "",
      },
    ],
  },
  {
    label: "Core Concepts",
    sections: [
      {
        id: "flows-and-decisions",
        title: "Flows & Decisions",
        Component: FlowsAndDecisionsMdx,
        headings: [...(metadata["flows-and-decisions"]?.headings ?? [])],
        rawContent: metadata["flows-and-decisions"]?.rawContent ?? "",
      },
      {
        id: "ui-affordances",
        title: "UI Affordances",
        Component: UiAffordancesMdx,
        headings: [...(metadata["ui-affordances"]?.headings ?? [])],
        rawContent: metadata["ui-affordances"]?.rawContent ?? "",
      },
      {
        id: "advanced-patterns",
        title: "Advanced Patterns",
        Component: AdvancedPatternsMdx,
        headings: [...(metadata["advanced-patterns"]?.headings ?? [])],
        rawContent: metadata["advanced-patterns"]?.rawContent ?? "",
      },
      {
        id: "observability",
        title: "Observability",
        Component: ObservabilityMdx,
        headings: [...(metadata["observability"]?.headings ?? [])],
        rawContent: metadata["observability"]?.rawContent ?? "",
      },
      {
        id: "dev-mode",
        title: "Dev Mode",
        Component: DevModeMdx,
        headings: [...(metadata["dev-mode"]?.headings ?? [])],
        rawContent: metadata["dev-mode"]?.rawContent ?? "",
      },
    ],
  },
  {
    label: "Reference",
    sections: [
      {
        id: "api-reference",
        title: "API Reference",
        Component: ApiReferenceMdx,
        headings: [...(metadata["api-reference"]?.headings ?? [])],
        rawContent: metadata["api-reference"]?.rawContent ?? "",
      },
      {
        id: "types",
        title: "Types",
        Component: TypesMdx,
        headings: [...(metadata.types?.headings ?? [])],
        rawContent: metadata.types?.rawContent ?? "",
      },
    ],
  },
];

// Flat list of all sections (preserves order from groups)
export const docSections: DocSection[] = docGroups.flatMap(
  (group) => group.sections,
);
